import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from './baseQuery'
import { apiConstants } from '../common/constants'

const apiBase = 'intervention'

export const interventionApi = createApi({
  reducerPath: 'interventionApi',
  baseQuery,
  keepUnusedDataFor: 5 * 60, // 5 minute cache
  endpoints: (builder) => ({

    getDashboardSummary: builder.query({
      query: (filters) => ({
        url: `${apiBase}/dashboard/summary`,
        data: filters,
        type: 'POST',
      }),
      transformResponse: (res) => res?.data || res,
    }),

    getCallAnalytics: builder.query({
      query: (filters) => ({
        url: `${apiBase}/calls/analytics`,
        data: filters,
        type: 'POST',
      }),
      transformResponse: (res) => res?.data || res,
    }),

    getCallRecordings: builder.query({
      query: (filters) => ({
        url: `${apiBase}/calls/recordings`,
        data: filters,
        type: 'POST',
      }),
      transformResponse: (res) => res?.data || res,
      keepUnusedDataFor: 60, // 1 minute for recordings
    }),

    getWhatsappAnalytics: builder.query({
      query: (filters) => ({
        url: `${apiBase}/whatsapp/analytics`,
        data: filters,
        type: 'POST',
      }),
      transformResponse: (res) => res?.data || res,
    }),

    getTemplates: builder.query({
      query: ({ type = 'ALL', channel = 'ALL' } = {}) => ({
        url: `${apiBase}/templates?type=${type}&channel=${channel}`,
        type: 'GET',
      }),
      transformResponse: (res) => res?.data?.templates || res?.templates || [],
    }),

    getRuleAnalytics: builder.query({
      query: (filters) => ({
        url: `${apiBase}/rules/analytics`,
        data: filters,
        type: 'POST',
      }),
      transformResponse: (res) => res?.data || res,
    }),

    getMerchantRuleConfig: builder.query({
      query: ({ merchantOid, type = 'NDR' }) => ({
        url: `${apiBase}/rules/config?merchantOid=${merchantOid}&type=${type}`,
        type: 'GET',
      }),
      transformResponse: (res) => res?.data || res,
    }),

    getCostAnalytics: builder.query({
      query: (filters) => ({
        url: `${apiBase}/cost/analytics`,
        data: filters,
        type: 'POST',
      }),
      transformResponse: (res) => res?.data || res,
    }),

    // NDR-scope summary (admin listing): TWO bulk calls total — the merchant
    // base + one config/search over merchant_config with an `includes`
    // projection (same pattern as order/search). NO per-merchant fan-out.
    // WhatsApp/Calling are EXACT merchant-panel pill parity: WhatsApp =
    // whatsappAutomatedNdr, Calling = futworksNdrConfig.pushLead ONLY (human
    // tele-calling — futworkAINdrConfig is deliberately skipped, same as the
    // merchant panel). "Prozo-managed" scope/KPI reads ndrManagedByProzo
    // directly — the authoritative field for that question, not inferred
    // from the toggles.
    getNdrScopeSummary: builder.query({
      queryFn: async (_arg, _api, _extra, bq) => {
        try {
          const [mRes, cRes] = await Promise.all([
            bq({
              url: apiConstants.MERCHANT_SEARCH_CACHED,
              data: {
                andfilter: [
                  { field: 'isTestMerchant', operator: 'ne', value: true },
                  { field: 'status', operator: 'eq', value: 'KYC_APPROVED' },
                ],
                offset: 0,
                limit: 20000,
                includes: 'id,name,is_deleted,companyName',
              },
              type: 'POST',
            }),
            bq({
              url: 'config/search',
              data: {
                andfilter: [],
                offset: 0,
                limit: 20000,
                includes: 'merchantOid,ndrManagedByProzo,whatsappAutomatedNdr,futworksNdrConfig,futworkAINdrConfig',
              },
              type: 'POST',
            }),
          ])

          const mBody = mRes?.data ?? mRes
          const merchants = (mBody?.data ?? mBody ?? [])
            .filter((m) => m.is_deleted === '0' && m.id)
            .map((m) => ({ id: m.id, name: m.name || m.companyName || '' }))

          const cBody = cRes?.data ?? cRes
          const flagsByOid = {}
          ;(cBody?.data ?? cBody ?? []).forEach((cfg) => {
            if (!cfg?.merchantOid) return
            flagsByOid[cfg.merchantOid] = {
              // WhatsApp: merchant-panel pill parity.
              whatsapp: !!cfg.whatsappAutomatedNdr,
              // Calling: human tele-calling ONLY — exact merchant-panel pill
              // parity. futworkAINdrConfig is fetched (per includes) but
              // deliberately not counted, same as the merchant panel.
              // NOTE: MerchantConfig$FutworksNdrConfig annotates its pushLead
              // field with @JsonProperty("push_lead") — the wire format is
              // snake_case even though the Java field name is camelCase. Read
              // push_lead (fallback to pushLead in case a future backend
              // change drops the annotation).
              calling: !!(cfg?.futworksNdrConfig?.push_lead ?? cfg?.futworksNdrConfig?.pushLead),
              // Fetched but NOT used for Scope/KPIs (kept for future reference).
              callingAi: !!(cfg?.futworkAINdrConfig?.push_lead ?? cfg?.futworkAINdrConfig?.pushLead),
              // Authoritative "is Prozo managing NDR for this merchant" — drives
              // the Scope chip and the Prozo-managed/Self-managed KPIs.
              managedByProzo: !!cfg.ndrManagedByProzo,
            }
          })

          // Merchants without a config doc = self-managed, everything off.
          const configs = {}
          merchants.forEach((m) => {
            configs[m.id] = flagsByOid[m.id] || { whatsapp: false, calling: false, callingAi: false, managedByProzo: false }
          })
          return { data: { merchants, configs } }
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.message || 'Failed to load NDR scope' } }
        }
      },
      keepUnusedDataFor: 30 * 60, // half-hour warm cache → instant on revisit
    }),
  }),
})

export const {
  useGetDashboardSummaryQuery,
  useGetCallAnalyticsQuery,
  useGetCallRecordingsQuery,
  useGetWhatsappAnalyticsQuery,
  useGetTemplatesQuery,
  useGetRuleAnalyticsQuery,
  useGetMerchantRuleConfigQuery,
  useGetCostAnalyticsQuery,
  useGetNdrScopeSummaryQuery,
} = interventionApi
